import { useRef, useCallback, useEffect } from 'react';

interface UseHoldRepeatOptions {
  delay?: number;
  interval?: number;
}

interface UseHoldRepeatReturn {
  start: () => void;
  stop: () => void;
}

const useHoldRepeat = (
  action: () => void,
  options: UseHoldRepeatOptions = {}
): UseHoldRepeatReturn => {
  const { delay = 300, interval = 100 } = options;

  const holdTimer = useRef<NodeJS.Timeout | null>(null);
  const repeatTimer = useRef<NodeJS.Timeout | null>(null);
  const isActive = useRef(false);
  const isCancelled = useRef(false);

  const start = useCallback(() => {
    if (isActive.current) return;
    isActive.current = true;
    isCancelled.current = false;

    action();

    holdTimer.current = setTimeout(() => {
      if (holdTimer.current) clearInterval(holdTimer.current);

      action();
      repeatTimer.current = setInterval(() => {
        if (isCancelled.current) return;

        action();
      }, interval);
    }, delay);
  }, [action, delay, interval]);

  const stop = useCallback(() => {
    if (!isActive.current) return;
    isActive.current = false;
    isCancelled.current = true;

    if (holdTimer.current !== null) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }

    if (repeatTimer.current !== null) {
      clearInterval(repeatTimer.current);
      repeatTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    start,
    stop,
  };
};

export default useHoldRepeat;
